import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BUILDER_TO_LOGO, builderSlug } from "../../constants/logoBuilderMap";
import { projectsAPI } from "../../services/api";

const GOLD = "#50C878";
const NAVY = "#013220";

// ─── DEVELOPERS HERO — 3-ROW LOGO CAROUSEL ───────────────────
// Shows the SAME 27 featured builders as the home-page carousel
// (sourced from BUILDER_TO_LOGO), laid across THREE equally-divided
// rows that scroll continuously (rows 1 & 3 left→right, row 2
// right→left). Each logo links to that builder's dedicated page.
//
// Display order is fixed (not alphabetical) via ORDERED_BUILDERS
// below — Row 1 → Row 2 → Row 3, 9 logos per row.
// ─────────────────────────────────────────────────────────────

// Desired display order. These are matched against BUILDER_TO_LOGO
// keys by running each name through builderSlug(), so it stays in
// sync with whatever slugs the map actually uses.
const ORDERED_BUILDERS = [
  // Row 1
  "Casagrand",
  "Supadha Infra Pvt Ltd",
  "Vertex Homes",
  "Janapriya Engineers Syndicate",
  "Lansum Group",
  "Reliance Builders",
  "Nikhila Constructions",
  "Team4 Life Spaces",
  "Mahaveer Group",
  // Row 2
  "Svadha Group",
  "Prosperiti Homes",
  "Oorjita Projects",
  "Vision Arsha",
  "Aspire Spaces",
  "Bhanodaya Constructions",
  "Hivision Developers",
  "SS Builders",
  "Profound Group",
  "Bharathi Constructions",
  // Row 3
  "Cozy Essence Builders",
  "Ananta",
  "Beams Columns",
  "Sri Developers",
  "GPR Farms and Ventures Pvt Ltd",
  "Sri Karthikeya Buggas",
  "vian",
  "Dnb Constructions",
  "Bliss Ventures Pvt",
  "Pacifica Group",
];

// Turn a slug back into a readable display name as a fallback,
// e.g. "sri-karthikeya-buggas" → "Sri Karthikeya Buggas".
function prettyName(slug) {
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function LogoTile({ builder, onClick }) {
  const { name, logo } = builder;

  return (
    <button
      onClick={() => onClick(builder)}
      title={name}
      style={{
        flexShrink: 0,
        width: "150px",
        height: "84px",
        margin: "0 10px",
        background: "#F7FBF8", // Light cream instead of pure white
        border: "1px solid #50c878",
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "12px 16px",
        cursor: "pointer",
        boxShadow: "0 4px 14px rgba(1,50,32,0.06)",
        transition: "transform 0.25s, box-shadow 0.25s, border-color 0.25s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = "0 10px 24px rgba(1,50,32,0.14)";
        e.currentTarget.style.borderColor = GOLD;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 4px 14px rgba(1,50,32,0.06)";
        e.currentTarget.style.borderColor = "#EAE2CC";
      }}
    >
      {logo ? (
        <img
          src={logo}
          alt={name}
          style={{
            maxWidth: "100%",
            maxHeight: "56px",
            objectFit: "contain",
            filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.15))", // Always add shadow
          }}
        />
      ) : (
        <span style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: "1.6rem", color: NAVY }}>
          {name.charAt(0)}
        </span>
      )}
    </button>
  );
}

// One continuously-scrolling row. Content is duplicated so the
// marquee loops seamlessly.
function MarqueeRow({ items, direction = "left", duration = 40, onClick }) {
  if (!items.length) return null;
  const loop = [...items, ...items];
  return (
    <div style={{ overflow: "hidden", width: "100%", padding: "6px 0", maskImage: "linear-gradient(to right, transparent, #000 8%, #000 92%, transparent)", WebkitMaskImage: "linear-gradient(to right, transparent, #000 8%, #000 92%, transparent)" }}>
      <div
        style={{
          display: "flex",
          width: "max-content",
          animation: `gbf-marquee-${direction} ${duration}s linear infinite`,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.animationPlayState = "paused")}
        onMouseLeave={(e) => (e.currentTarget.style.animationPlayState = "running")}
      >
        {loop.map((b, i) => (
          <LogoTile key={`${b.slug}-${i}`} builder={b} onClick={onClick} />
        ))}
      </div>
    </div>
  );
}

export default function DevelopersLogoCarousel() {
  const navigate = useNavigate();
  // Map slug → real builder_name from the API (for nicer display text).
  const [nameBySlug, setNameBySlug] = useState({});

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await projectsAPI.list();
        const list = res?.data || res?.projects || res || [];
        if (!active) return;
        const map = {};
        for (const p of list) {
          const name = (p.builder_name || "").trim();
          if (!name) continue;
          const slug = builderSlug(name);
          if (slug in BUILDER_TO_LOGO && !map[slug]) map[slug] = name;
        }
        setNameBySlug(map);
      } catch (_) {
        // Fall back to slug-derived names.
      }
    })();
    return () => { active = false; };
  }, []);

  // Build the ordered builder list from ORDERED_BUILDERS.
  // Each name is slugified and looked up in BUILDER_TO_LOGO.
  // Any ORDERED_BUILDERS entry whose slug isn't in the map is skipped,
  // and any map keys not covered by ORDERED_BUILDERS are appended at
  // the end (so nothing silently disappears if a name doesn't match).
  const builders = useMemo(() => {
    const seen = new Set();
    const ordered = [];

    for (const name of ORDERED_BUILDERS) {
      const slug = builderSlug(name);
      if (slug in BUILDER_TO_LOGO && !seen.has(slug)) {
        seen.add(slug);
        ordered.push({
          slug,
          name: nameBySlug[slug] || name,
          logo: BUILDER_TO_LOGO[slug] || null,
        });
      }
    }

    // Append any builders in the map that weren't matched above.
    for (const slug of Object.keys(BUILDER_TO_LOGO)) {
      if (!seen.has(slug)) {
        seen.add(slug);
        ordered.push({
          slug,
          name: nameBySlug[slug] || prettyName(slug),
          logo: BUILDER_TO_LOGO[slug] || null,
        });
      }
    }

    return ordered;
  }, [nameBySlug]);

  // Split into 3 EQUALLY-divided rows.
  const rows = useMemo(() => {
    const all = builders;
    const perRow = Math.ceil(all.length / 3);
    return [all.slice(0, perRow), all.slice(perRow, perRow * 2), all.slice(perRow * 2)];
  }, [builders]);

  function openBuilder(b) {
    if (b?.slug) navigate(`/builder/${b.slug}`);
  }

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "8px" }}>
      <MarqueeRow items={rows[0]} direction="left" duration={45} onClick={openBuilder} />
      <MarqueeRow items={rows[1]} direction="right" duration={52} onClick={openBuilder} />
      <MarqueeRow items={rows[2]} direction="left" duration={48} onClick={openBuilder} />

      <style>{`
        @keyframes gbf-marquee-left {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes gbf-marquee-right {
          0%   { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          div[style*="gbf-marquee"] { animation: none !important; }
        }
      `}</style>
    </div>
  );
}